import { create } from 'zustand'

/**
 * Interface state with no server representation and no place in the url.
 *
 * One thing: which theme to paint. The server has no opinion about it and no route
 * names it, and it has to survive a reload to be worth anything, so it is mirrored
 * into `localStorage`.
 */
const THEME_KEY = 'lithos.theme'

/**
 * `system` is a choice, not the absence of one.
 *
 * Held as three values rather than a boolean because "follow the operating
 * system" is a state that has to survive the system changing its mind, and a
 * boolean resolved once at startup cannot express it.
 */
export type Theme = 'system' | 'light' | 'dark'

const THEMES: readonly Theme[] = ['system', 'light', 'dark']

function readTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_KEY)
    // Anything else was written by another version of this app, or by hand.
    return THEMES.includes(raw as Theme) ? (raw as Theme) : 'system'
  } catch {
    // Storage access throws outright in a few configurations -- Safari with
    // cookies blocked is the common one. A theme that does not persist beats a
    // shell that will not render.
    return 'system'
  }
}

function writeTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme)
  } catch {
    // As above.
  }
}

export interface UiState {
  /** Which palette to paint, or to take from the operating system. */
  theme: Theme
}

export interface UiActions {
  setTheme: (theme: Theme) => void
}

export const useUiStore = create<UiState & UiActions>((set) => ({
  theme: readTheme(),

  setTheme: (theme) => {
    writeTheme(theme)
    set({ theme })
  },
}))

/** Test seam: a fresh tab that has never chosen a theme. */
export function __resetUiForTests(): void {
  try {
    localStorage.removeItem(THEME_KEY)
  } catch {
    // As above.
  }
  useUiStore.setState({ theme: 'system' })
}
